import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Api from '../utils/Api';
import { useAuth } from './AuthContext';
import { useError } from './ErrorContext';
import { useLoading } from './LoadingContext';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useRoutePath } from '../utils/getRoutePath';

const ProductCategoryContext = createContext();
 
export const useProductCategories = () => {
  const context = useContext(ProductCategoryContext);
  if (!context) {
    throw new Error('useProductCategories must be used within a ProductCategoryProvider');
  }
  return context;
};

export const ProductCategoryProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hideLoading } = useLoading();
  const { getToken } = useAuth();
  const [ setApi] = useState(null);
  const { handleApiError, formErrors, clearErrors } = useError();
  const [productCategories, setProductCategories] = useState([]);
  const segments = location.pathname.split('/');
  const id = segments.pop();
  const [paginate, setPaginate] = useState(true);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/product-categories');

  const getProductCategories = useCallback(async (page = 1, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
   
    try {
      const searchParams = new URLSearchParams(window.location.search);
      let query = '';
      if(filters?.q){
        query = '&q='+filters?.q;
        
      } else{
        const q = searchParams.get('q') || '';
        query = '&q='+q;
      }
      if(filters?.active){
        query += '&active='+filters?.active;
      } else{
        const active = searchParams.get('active') || '';
        query += '&active='+active;
      }
      setPaginate(true)
      let response = await apiInstance.call('product-categories?page=' + page+'&paginate='+paginate +''+query , 'GET');
      if(filters?.all){
        query = '';
        setPaginate(false)
        if(filters?.active){
          if(filters?.active === true){
            query += 'active=1';
          }else{
            query += 'active=0';
          }
         
        }
        response = await apiInstance.call('product-categories?'+query);  
      }
      
      if (response.status === 200) {
       // setPaginate(response.data.paginate)
        setProductCategories(response.data.productCategories || []);
       
        
        if(filters){
          return response.data.productCategories;
        }
      } else {
        
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch product categories information'
        });
      }
    } catch (error) {
     
    } finally {
      
    }
  }, [handleApiError, getToken,paginate]);

  const getProductCategoryByIdApi = useCallback(async (id, filters = {}) => {
    
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call(`product-categories/${id}/edit`, 'GET', null, true, false);
   
    if (response.status === 200) {
      
      let productCategories = {'data':[response.data.productCategory]}
      setProductCategories(productCategories || []);
      
    } 
  }, [getToken]);

  useEffect(() => {
    
    const handleLoad = async() => {
      const currentPath = window.location.pathname;
      
      if(currentPath.includes(getRoutePath('/product-categories/edit/'+id))){
        if (getToken()) {
        
          
          await getProductCategoryByIdApi(id);
        }
      }
    };

    // Add onload listener
    window.addEventListener('load', handleLoad);

    // Cleanup listener
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [getRoutePath, id, getToken, getProductCategoryByIdApi, searchParams,setApi]);

  // Initialize API and load initial data
  useEffect(() => {
    const initializeApi = async () => {
      try {
     
        if (getToken()) {
          let page = parseInt(searchParams.get('page')) || 1;
         
          await getProductCategories(page);
        }
      } catch (error) {
        hideLoading();
      }
    };
   
    if(initialized){
      initializeApi();
    }
  }, [getToken, getProductCategories, searchParams, initialized,hideLoading,setApi]);

    
  const addProductCategory = async (data) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {

      let formData = new FormData();

      // Append image if provided
      if (data.image) {
        formData.append('image', data.image);
      }
      
      // Append the rest of the data as JSON
      formData.append('data', JSON.stringify(data));

      const response = await apiInstance.call('product-categories', 'POST', formData, true,true);
      
      if (response.status === 201 || response.status === 200) {
        // Refresh the product categories list
        await getProductCategories();
        return { status: response.status, data: response.data };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to add Product Category'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to create product category'
      });
      return { status: 500, error: error.message };
    }
  };
  
  const getProductCategoryById = useCallback(async (id) => {
   // let category = productCategories?.data?.find(productCategory => Number(productCategory.id) === Number(id));
 
    //return {'status':200,'data':category};
    
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`product-categories/${id}`, 'GET', null, true);
      
      if (response.status === 200) {
        return { status: response.status, data: response.data.productCategory };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to fetch product category details'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to fetch product category details'
      });
      return { status: 500, error: error.message };
    }
  }, [getToken, handleApiError]);

  const updateProductCategory = async (id, updatedData) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      let formData = new FormData();

      // Append image if provided
      if (updatedData.image) {
        formData.append('image', updatedData.image);
      }
      
      // Append the rest of the data as JSON
      formData.append('data', JSON.stringify(updatedData));
      
      // Now send the request with formData only once
      const response = await apiInstance.call(`product-categories/${id}`, 'POST', formData, true, true);
      
      if (response.status === 200) {
        // Refresh the product categories list
        await getProductCategories();
        return { status: response.status, data: response.data };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
             errors: response.error.validationErrors,
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to update Product Category'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to update product category'
      });
      return { status: 500, error: error.message };
    }
  };

  const deleteProductCategory = async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`product-categories/${id}`, 'DELETE', null, true);
      
      if (response.status === 200) {
        // Refresh the product categories list
        await getProductCategories();
        navigate(getRoutePath('/product-categories'));
        return { status: response.status, data: response.data };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to delete product category'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to delete product category'
      });
      return { status: 500, error: error.message };
    }
  };

  return (
    <ProductCategoryContext.Provider
      value={{
        productCategories,
        addProductCategory,
        getProductCategoryById,
        updateProductCategory,
        deleteProductCategory,
        getProductCategories,
        formErrors,
        clearErrors,
        setPaginate,
        paginate
      }}
    >
      {children}
    </ProductCategoryContext.Provider>
  );
};

export default ProductCategoryContext; 